import { createFeatureSelector, createSelector } from '@ngrx/store';
import { RouterReducerState } from '@ngrx/router-store';
import { selectAuth } from './auth/auth.reducers';
import { selectEnrollmentRequired } from './twoFactor/two-factor.reducers';
import { RouterStateUrl } from './router/router.state';
import { isAppPath, routePathOf } from 'src/app/auth/utils/return-url';

/** Object-typed view of the NgRx router-store slice (the State-typed selectRouterState in
 *  store.reducers can't combine with the object-typed feature selectors used here). */
const selectRouter = createFeatureSelector<RouterReducerState<RouterStateUrl>>('router');

/**
 * True when the user is on an in-app route (`/app/**`). This is the reactive NgRx router-store URL,
 * which only becomes `/app/...` AFTER navigation + the route guards run — so it is NEVER true on
 * `/auth/login` or `/auth/enroll`.
 *
 * This "after guards" guarantee depends on `navigationActionTiming: PostActivation` in StoreModule
 * (store.module.ts). Under the ngrx DEFAULT (PreActivation) the URL updates BEFORE guards, so a
 * navigation the enrollmentRequiredGuard cancels would still flip this true — do not remove that config.
 *
 * Use this (not `auth.isAuthenticated`) to gate per-login data loads. `isAuthenticated` flips true
 * the instant a token is applied — including the restricted forced-enrollment token, and the brief
 * window on `/auth/login` before the router navigates to `/app` — so auth-flag gates fire during the
 * auth flow and either flash UI (release-notes dialog) or hit endpoints the session can't yet reach
 * (`/v2/userSettings` → 401 → session teardown).
 */
export const selectIsOnAppRoute = createSelector(
  selectRouter,
  (router) => {
    // Segment-safe match via the shared isAppPath (single source of truth with return-url.ts): `/app`
    // exactly, or a `/app/` sub-route. A loose `startsWith('/app')` would also match a sibling like
    // `/apps` or `/application`, prematurely firing the per-login loaders on a non-app page.
    const url = router?.state?.url;
    if (typeof url !== 'string') return false;
    // Shared query/fragment strip (routePathOf) rather than an inline split, so this and
    // AuthSyncService's identity-switch check can't drift on what "the path" means.
    return isAppPath(routePathOf(url));
  },
);

/**
 * App-ready signal for per-login loaders: authenticated, actually on an `/app` route, AND NOT in a
 * forced-2FA-enrollment session.
 *
 * The `!enrollmentRequired` term makes this a true "session can reach /app endpoints" signal rather than
 * a route proxy. A restricted forced-enrollment token flips `isAuthenticated` true but is scoped to the
 * enrollment endpoints only; normally the wizard also lives on `/auth/enroll` so `onApp` already excludes
 * it — but an admin flipping require-2FA mid-session mints an enrollment token WITHOUT the tab leaving
 * `/app`, so the route term alone would keep this true. A per-login consumer keyed off this signal would
 * then fire its endpoint under the restricted bearer → 401 → session teardown. Excluding enrollment here
 * closes that window for every consumer at once (stats-stream keeps its own token-aware gate — it must
 * react without a route change and also needs the bearer, which this store-only selector doesn't carry).
 */
export const selectIsFullyAuthenticated = createSelector(
  selectAuth,
  selectIsOnAppRoute,
  selectEnrollmentRequired,
  (auth, onApp, enrollmentRequired) => auth.isAuthenticated && onApp && !enrollmentRequired,
);
